import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Button,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Timeline, TimelineItem, TimelineContent, TimelineSeparator, TimelineDot, TimelineConnector } from '@mui/lab';
import ManagingCommitteeDisplay from '../../components/ManagingCommitteeDisplay';

function About() {


  const statistics = [
    {
      value: '2500+',
      label: 'Active Members',
      description: 'Dedicated legal professionals in our community'
    },
    {
      value: '40+',
      label: 'Years of Excellence',
      description: 'Serving justice since 1950'
    },
    {
      value: '1000+',
      label: 'Pro Bono Cases',
      description: 'Annual free legal aid services'
    },
    {
      value: '30+',
      label: 'Annual Events',
      description: 'Including seminars, workshops, and conferences'
    }
  ];

  const milestones = [
    {
      date: '2024',
      title: 'Digital Transformation',
      description: 'Launched state-of-the-art digital facilities for members including e-library and virtual meeting rooms.'
    },
    {
      date: '2020',
      title: 'Golden Jubilee Celebration',
      description: 'Celebrated 70 years of excellence with a grand ceremony and legal conference.'
    },
    {
      date: '2015',
      title: 'Legal Aid Center Expansion',
      description: 'Established dedicated legal aid centers across Andheri to provide free legal assistance.'
    },
    {
      date: '2010',
      title: 'Infrastructure Development',
      description: 'Modernized bar room facilities and library with digital resources.'
    },
    {
      date: '2000',
      title: 'Membership Milestone',
      description: 'Crossed 500 active members, marking significant growth in the legal community.'
    },
    {
      date: '1950',
      title: 'Foundation',
      description: "Establishment of Andheri Court's Bar Association with 50 founding members."
    }
  ];

  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month < 4 ? `${year-1}-${year}` : `${year}-${year+1}`;
  };

  useEffect(() => {
    const fetchCommitteeMembers = async () => {
      try {
        const currentYear = getCurrentFinancialYear();
        const docRef = doc(db, 'managingCommittee', currentYear);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const yearData = docSnap.data();
          const membersArray = yearData.members || [];
          // Sort members by position
          const positions = ['PRESIDENT', 'VICE-PRESIDENT', 'SECRETARY', 'JOINT-SECRETARY', 'TREASURER'];
          membersArray.sort((a, b) => {
            const posA = positions.indexOf(a.position);
            const posB = positions.indexOf(b.position);
            if (posA === -1 && posB === -1) return a.name.localeCompare(b.name);
            if (posA === -1) return 1;
            if (posB === -1) return -1;
            return posA - posB;
          });
          setCommitteeMembers(membersArray);
        }
      } catch (error) {
        console.error('Error fetching committee members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommitteeMembers();
  }, []);

  const getPositionDisplay = (position) => {
    const displayMap = {
      'PRESIDENT': 'President',
      'VICE-PRESIDENT': 'Vice President',
      'SECRETARY': 'Secretary',
      'JOINT-SECRETARY': 'Joint Secretary',
      'TREASURER': 'Treasurer',
      'CO-OPTED MEMBER': 'Co-opted Member'
    };
    return displayMap[position] || position;
  };

  const president = committeeMembers.find(m => m.position === 'PRESIDENT');
  const mainMembers = committeeMembers.filter(m => 
    m.position !== 'PRESIDENT' && 
    m.position !== 'CO-OPTED MEMBER'
  );
  const coOptedMembers = committeeMembers.filter(m => m.position === 'CO-OPTED MEMBER');

  return (
    <Box sx={{ mt: '-64px' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '250px' },
          width: '100%',
          overflow: 'hidden',
          mb: 0,
          pt: '64px',
          bgcolor: '#580100',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/about-banner.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
            zIndex: 0
          }
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            About Us
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}


      {/* Advocating for Justice Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              color: '#580100',
              fontWeight: 600,
              mb: 3
            }}>
              Advocating
              <br />
              for Justice
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'justify' }}>
              Welcome to the Andheri Court's Bar Association, a prestigious institution dedicated to upholding justice and empowering the legal community. Our association is a collective of passionate legal professionals committed to fostering collaboration, integrity, and advancing the principles of law in society.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/hero-bg.png"
              alt="Andheri Court's Bar Association"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                mx: 'auto'
              }}
            />
          </Grid>
       
        </Grid>
      </Container>

      {/* About Section */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={12}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ 
                color: '#580100',
                fontWeight: 600,
                mb: 3,
              }}>
                About Andheri Court's Bar Association
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'justify' }}>
                The Andheri Court's Bar Association is a registered professional body representing advocates practicing at the Judicial Magistrate Court, Andheri (East), Mumbai. Established to uphold the highest standards of legal practice, our association serves as a cornerstone for criminal law practitioners in one of Mumbai's busiest judicial districts.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'justify' }}>
                With jurisdiction over criminal matters across Eight (8) dedicated courts, our members specialize in navigating the complexities of criminal litigation, including trials, bail proceedings, and applications under the Negotiable Instrument Act, Bhartiya Nyaya Sanhita, 2023 (Previously Indian Penal Code i.e. IPC) and several other acts and allied laws. Governed by an elected executive committee, we are committed to fostering ethical practice, professional growth, and seamless collaboration among legal professionals.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', textAlign: 'justify' }}>
                As a unified platform, we strive to:
              </Typography>
              <List sx={{ mb: 3, pl: 2 }}>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary="Empower Advocates: Provide resources, training, and networking opportunities to enhance courtroom excellence." />
                </ListItem>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary="Serve the Community: Ensure accessible and efficient legal representation for all stakeholders." />
                </ListItem>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary="Advance Justice: Uphold the rule of law through rigorous advocacy and adherence to judicial ethics." />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Leadership Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                color: '#580100',
                fontWeight: 700,
                mb: 2
              }}
            >
              Leadership
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                textAlign: 'justify'
              }}
            >
              Meet our dedicated team of legal professionals leading the Andheri Court's Bar Association.
            </Typography>
          </Box>

          {/* Committee Members Grid */}
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ManagingCommitteeDisplay members={committeeMembers} showContactInfo={true} showCoOptedMembers={true} />
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ bgcolor: 'background.paper', p: 6, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Our Impact in Numbers
          </Typography>
          <Typography variant="body1" align="center" paragraph sx={{ mb: 4 }}>
            At the Andheri Court's Bar Association, we take pride in our contributions to the legal community and society at large.
          </Typography>
          <Grid container spacing={4}>
            {statistics.map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <Box sx={{ 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Typography variant="h3" component="div" color="primary" gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ color: '#580100', fontWeight: 600 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Timeline Section */}
      {/* <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Our Journey Through Time
          </Typography>
          <Typography variant="body1" align="center" paragraph sx={{ mb: 6, textAlign: 'justify' }}>
            Discover the key milestones that have shaped our association's legacy of excellence.
          </Typography>
          <Timeline position="alternate">
            {milestones.map((milestone, index) => (
              <TimelineItem key={milestone.date}>
                <TimelineSeparator>
                  <TimelineDot color="primary" variant={index === 0 ? "filled" : "outlined"} />
                  {index < milestones.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="h6" component="h3" color="primary">
                      {milestone.date}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'justify' }}>
                      {milestone.description}
                    </Typography>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </Box> */}

      {/* Membership Section */}
      <Box
        sx={{
          bgcolor: '#580100',
          color: 'white',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Join Our Legal Community
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'justify'
                }}
              >
                Become a part of Andheri's most prestigious legal association and contribute to the advancement of justice.
              </Typography>
            </Grid>
            <Grid 
              item 
              xs={12} 
              md={4} 
              sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' } 
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: '#580100',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  textTransform: 'none',
                  zIndex: 1
                }}
              >
                Become a Member
              </Button>
            </Grid>
          </Grid>
        </Container>

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'url(/images/pattern.png) repeat',
            opacity: 0.1,
            zIndex: 0
          }}
        />
      </Box>

      {/* Acknowledgment Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            maxWidth: 900, 
            mx: 'auto',
            textAlign: 'center',
            mb: 6
          }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                color: '#580100',
                mb: 4,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Acknowledgment of Contributions
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ 
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                mb: 4,
                mx: 'auto',
                maxWidth: '800px'
              }}
            >
              We extend our heartfelt gratitude to the Managing Committee of the Andheri Court's Bar Association (2024-2025) for their invaluable contributions and leadership in initiating the creation of this website. Their vision and efforts aim to enhance accessibility and communication for all our members and visitors.
            </Typography>
          </Box>

          <Box sx={{ 
            maxWidth: 800, 
            mx: 'auto',
            mb: 6,
            p: 6,
            bgcolor: 'rgba(88, 1, 0, 0.03)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(88, 1, 0, 0.1)'
          }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 600, 
                color: '#580100',
                mb: 4,
                textAlign: 'center'
              }}
            >
              Managing Committee (2024-2025)
            </Typography>
            
            {/* President Section */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: 5,
              pb: 4,
              borderBottom: '1px solid',
              borderColor: 'rgba(88, 1, 0, 0.1)'
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#580100',
                  fontWeight: 500,
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
              >
                President
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 600,
                  color: '#580100',
                  fontSize: { xs: '1.75rem', md: '2rem' }
                }}
              >
                Kashinath R. Tripathi
              </Typography>
            </Box>
            
            <Grid container spacing={4} justifyContent="center">
              {/* Office Bearers */}
              <Grid item xs={12} md={6}>
                <List>
                  <ListItem sx={{ textAlign: 'center', flexDirection: 'column', py: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#580100',
                        fontWeight: 500,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Vice-President
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      Rajkumar L. Upadhyay
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ textAlign: 'center', flexDirection: 'column', py: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#580100',
                        fontWeight: 500,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Secretary
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      Indresh Kumar Tiwari
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List>
                  <ListItem sx={{ textAlign: 'center', flexDirection: 'column', py: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#580100',
                        fontWeight: 500,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Joint-Secretary
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      Sanjay Kumar Mishra
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ textAlign: 'center', flexDirection: 'column', py: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#580100',
                        fontWeight: 500,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Treasurer
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      Santosh Kumar Pandey
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            {/* Members */}
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 4, 
                mb: 2, 
                color: '#580100',
                fontWeight: 600,
                textAlign: 'center'
              }}
            >
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    textAlign: 'center',
                    color: '#580100',
                    fontWeight: 500,
                    mb: 0,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Members
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Pramila Ashutosh Dubey
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Archana Singh
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Pradeep Kumar Pandey
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Vinod Kumar R. Yadav
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Ashutosh C. Shukla
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ justifyContent: 'center' }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                      Brijesh J. Gupta
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ 
            maxWidth: 900, 
            mx: 'auto',
            textAlign: 'center'
          }}>
            <Typography 
              variant="body1"
              sx={{ 
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                textAlign: 'justify'
              }}
            >
              Their initiative has laid the foundation for a modern and user-friendly platform, bringing the Andheri Court's Bar Association closer to its members and the community.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default About;
